import { forwardRef, useMemo } from 'react';
import type { EstimateWithRelations } from '@/types/estimate';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { resolveTaxConfig } from '@/utils/tax';
import { DocumentSheet } from '@/components/documents/DocumentSheet';
import { buildEstimateDocument } from '@/components/documents/adapters';

interface EstimatePreviewProps {
  estimate: EstimateWithRelations;
  variant?: 'print' | 'screen';
}

/**
 * Printable A4 quotation. Thin wrapper: resolves tenant profile, tax snapshot
 * and locale, then hands a DocumentModel to the shared DocumentSheet renderer.
 */
export const EstimatePreview = forwardRef<HTMLDivElement, EstimatePreviewProps>(
  ({ estimate, variant = 'print' }, ref) => {
    const { data: company } = useCompanyProfile();
    const liveTax = useCompanyTax();
    const { locale, timezone } = useCompanyLocale();

    const doc = useMemo(
      () => buildEstimateDocument(estimate, {
        company,
        tax: resolveTaxConfig(estimate, liveTax),
        locale,
        timezone,
      }),
      [estimate, company, liveTax, locale, timezone],
    );

    return <DocumentSheet ref={ref} doc={doc} variant={variant} />;
  },
);

EstimatePreview.displayName = 'EstimatePreview';
